import { forwardRef, useId } from "react";
import styles from "./Field.module.css";

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Hide the label visually but keep it for screen readers. */
  hideLabel?: boolean;
  icon?: React.ReactNode;
  hint?: string;
  error?: string | null;
}

/**
 * Labelled text input with the journey-1 "icon tab" treatment — a tinted square
 * on the left holding an icon, then the field. Fully accessible.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hideLabel, icon, hint, error, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errId = error ? `${fieldId}-err` : undefined;

  return (
    <div className={[styles.field, className].filter(Boolean).join(" ")}>
      <label
        htmlFor={fieldId}
        className={hideLabel ? "ap-sr-only" : styles.label}
      >
        {label}
      </label>
      <div className={[styles.control, error ? styles.invalid : ""].join(" ")}>
        {icon && (
          <span className={styles.iconTab} aria-hidden="true">
            {icon}
          </span>
        )}
        <input
          ref={ref}
          id={fieldId}
          className={styles.input}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, errId].filter(Boolean).join(" ") || undefined}
          {...rest}
        />
      </div>
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hideLabel?: boolean;
  error?: string | null;
  children: React.ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField({ label, hideLabel, error, id, className, children, ...rest }, ref) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const errId = error ? `${fieldId}-err` : undefined;
    return (
      <div className={[styles.field, className].filter(Boolean).join(" ")}>
        <label htmlFor={fieldId} className={hideLabel ? "ap-sr-only" : styles.label}>
          {label}
        </label>
        <div className={[styles.control, styles.selectControl, error ? styles.invalid : ""].join(" ")}>
          <select
            ref={ref}
            id={fieldId}
            className={styles.select}
            aria-invalid={error ? true : undefined}
            aria-describedby={errId}
            {...rest}
          >
            {children}
          </select>
          <span className={styles.chevron} aria-hidden="true">▾</span>
        </div>
        {error && (
          <p id={errId} className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
